import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { 
  FaUsers, 
  FaUserShield, 
  FaBuilding, 
  FaFileAlt, 
  FaTags, 
  FaFile, 
  FaSignOutAlt,
  FaBars,
  FaTimes,
  FaHome,
  FaChartBar,
  FaCalendarAlt,
  FaUserCheck
} from 'react-icons/fa';
import axios from 'axios';
import { API_ENDPOINTS } from '../config/api';
import UnifiedPublicationList from './UnifiedPublicationList';
import PublicationForm from './PublicationForm';
import PublicationDetail from './PublicationDetail';
import UserManagement from './admin/UserManagement';
import RoleManagement from './admin/RoleManagement';
import DepartmentManagement from './admin/DepartmentManagement';
import PublicationTypeManagement from './admin/PublicationTypeManagement';
import CategoryManagement from './admin/CategoryManagement';
import DocumentTypeManagement from './admin/DocumentTypeManagement';
import WeeklyHighlightsManagement from './admin/WeeklyHighlightsManagement';
import WeeklyHighlightsForm from './admin/WeeklyHighlightsForm';
import LegalDocumentsManagement from './admin/LegalDocumentsManagement';
import ModalTest from './admin/ModalTest';
import './Dashboard.css';

const Dashboard = () => {
  const { user, logout, isAdmin, isSuperAdmin, hasWeeklyHighlightAccess } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Debug logging
  console.log('Dashboard Debug:', {
    user: user?.name,
    isAdmin,
    isSuperAdmin,
    hasWeeklyHighlightAccess,
    currentPath: location.pathname,
    userPermissions: {
      can_manage_publications: user?.can_manage_publications,
      can_manage_users: user?.can_manage_users,
      can_manage_departments: user?.can_manage_departments,
      can_manage_publication_types: user?.can_manage_publication_types,
      can_manage_categories: user?.can_manage_categories,
      can_manage_document_types: user?.can_manage_document_types,
      can_manage_weekly_highlights: user?.can_manage_weekly_highlights,
      can_manage_legal_documents: user?.can_manage_legal_documents
    },
    fullUserObject: user
  });

  // Immediate redirect for users with only Weekly Highlight access
  useEffect(() => {
    if (hasWeeklyHighlightAccess && !isAdmin && location.pathname !== '/dashboard/weekly-highlights') {
      console.log('Redirecting user to Weekly Highlights...');
      navigate('/dashboard/weekly-highlights', { replace: true });
    }
  }, [hasWeeklyHighlightAccess, isAdmin, location.pathname, navigate]);



  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const navigationItems = [
    {
      name: 'Dashboard',
      path: '/dashboard',
      icon: <FaHome />,
      show: true
    },
    {
      name: 'Publications',
      path: '/dashboard/publications',
      icon: <FaFileAlt />,
      show: user?.can_manage_publications
    },
    {
      name: 'User Management',
      path: '/dashboard/users',
      icon: <FaUsers />,
      show: user?.can_manage_users
    },
    {
      name: 'Role Management',
      path: '/dashboard/roles',
      icon: <FaUserShield />,
      show: isSuperAdmin
    },
    {
      name: 'Department Management',
      path: '/dashboard/departments',
      icon: <FaBuilding />,
      show: user?.can_manage_departments
    },
    {
      name: 'Publication Types',
      path: '/dashboard/publication-types',
      icon: <FaFileAlt />,
      show: user?.can_manage_publication_types
    },
    {
      name: 'Categories',
      path: '/dashboard/categories',
      icon: <FaTags />,
      show: user?.can_manage_categories
    },
    {
      name: 'Document Types',
      path: '/dashboard/document-types',
      icon: <FaFile />,
      show: user?.can_manage_document_types
    },
    {
      name: 'Weekly Highlights',
      path: '/dashboard/weekly-highlights',
      icon: <FaCalendarAlt />,
      show: user?.can_manage_weekly_highlights
    },
    {
      name: 'Legal Documents',
      path: '/dashboard/legal-documents',
      icon: <FaFileAlt />,
      show: user?.can_manage_legal_documents
    },
  ];

  return (
    <div className="dashboard-container">
      {/* Sidebar */}
      <div className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <h2>PMS</h2>
          <button className="sidebar-toggle" onClick={toggleSidebar}>
            <FaTimes />
          </button>
        </div>
        
        <div className="user-info">
          <div className="user-avatar">
            {user?.name?.charAt(0).toUpperCase()}
          </div>
          <div className="user-details">
            <h4>{user?.name}</h4>
            <p>{user?.role}</p>
            <small>{user?.department}</small>
          </div>
        </div>
        
        <nav className="sidebar-nav horizontal">
          {(() => {
            const visibleItems = navigationItems.filter(item => item.show);
            console.log('Navigation Debug:', {
              allItems: navigationItems.map(item => ({ 
                name: item.name, 
                show: item.show, 
                permission: item.name === 'Publications' ? user?.can_manage_publications : 
                           item.name === 'User Management' ? user?.can_manage_users :
                           item.name === 'Department Management' ? user?.can_manage_departments :
                           item.name === 'Publication Types' ? user?.can_manage_publication_types :
                           item.name === 'Categories' ? user?.can_manage_categories :
                           item.name === 'Document Types' ? user?.can_manage_document_types :
                           item.name === 'Weekly Highlights' ? user?.can_manage_weekly_highlights :
                           item.name === 'Legal Documents' ? user?.can_manage_legal_documents : 'N/A'
              })),
              visibleItems: visibleItems.map(item => item.name),
              currentPath: location.pathname
            });
            
            return visibleItems.map((item) => {
              const isActive = location.pathname === item.path || 
                (item.path !== '/dashboard' && location.pathname.startsWith(item.path));
              return (
                <button
                  key={item.path}
                  className={`nav-item ${isActive ? 'active' : ''}`}
                  onClick={() => {
                    navigate(item.path);
                    setSidebarOpen(false);
                  }}
                >
                  {item.icon}
                  <span>{item.name}</span>
                </button>
              );
            });
          })()}
        </nav>
        
        <div className="sidebar-footer">
          <button className="logout-button" onClick={handleLogout}>
            <FaSignOutAlt />
            <span>Logout</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="main-content">
        <header className="top-header">
          <button className="mobile-menu-toggle" onClick={toggleSidebar}>
            <FaBars />
          </button>
          <h1>Publication Management System</h1>
        </header>
        
        <main className="content">
          <Routes>
            <Route path="/" element={<DashboardHome />} />
            <Route path="/publications" element={
              (() => {
                if (isAdmin || !hasWeeklyHighlightAccess) {
                  return <UnifiedPublicationList isPublic={false} />;
                } else {
                  return <Navigate to="/dashboard/weekly-highlights" replace />;
                }
              })()
            } />
            <Route path="/add-publication" element={
              (() => {
                if (isAdmin || !hasWeeklyHighlightAccess) {
                  return <PublicationForm />;
                } else {
                  return <Navigate to="/dashboard/weekly-highlights" replace />;
                }
              })()
            } />
            <Route path="/publications/:identifier" element={
              (() => {
                if (isAdmin || !hasWeeklyHighlightAccess) {
                  return <PublicationDetail />;
                } else {
                  return <Navigate to="/dashboard/weekly-highlights" replace />;
                }
              })()
            } />
            <Route path="/publications/:identifier/edit" element={
              (() => {
                if (isAdmin || !hasWeeklyHighlightAccess) {
                  return <PublicationForm editMode={true} />;
                } else {
                  return <Navigate to="/dashboard/weekly-highlights" replace />;
                }
              })()
            } />
            <Route path="/users" element={<UserManagement />} />
            <Route path="/roles" element={<RoleManagement />} />
            <Route path="/departments" element={<DepartmentManagement />} />
            <Route path="/publication-types" element={<PublicationTypeManagement />} />
            <Route path="/categories" element={<CategoryManagement />} />
            <Route path="/document-types" element={<DocumentTypeManagement />} />
            <Route path="/weekly-highlights" element={<WeeklyHighlightsManagement />} />
            <Route path="/weekly-highlights/create" element={<WeeklyHighlightsForm />} />
            <Route path="/weekly-highlights/edit/:identifier" element={<WeeklyHighlightsForm />} />
            <Route path="/legal-documents" element={<LegalDocumentsManagement />} />
            <Route path="/modal-test" element={<ModalTest />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

// Dashboard Home Component
const DashboardHome = () => {
  const { user, isAdmin, hasWeeklyHighlightAccess } = useAuth();
  const [statistics, setStatistics] = useState(null);
  const [publicationTypes, setPublicationTypes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        
        if (!token) {
          setLoading(false);
          return;
        }

        // Fetch publication types from Publication Type Management
        const publicationTypesResponse = await axios.get(API_ENDPOINTS.PUBLICATION_TYPES, {
          headers: {
            'Authorization': `Bearer ${token}`
          },
          timeout: 10000
        });

        console.log('Publication types response:', publicationTypesResponse.data);
        setPublicationTypes(publicationTypesResponse.data);

        // Fetch statistics for counts
        const statisticsResponse = await axios.get(API_ENDPOINTS.STATISTICS, {
          headers: {
            'Authorization': `Bearer ${token}`
          },
          timeout: 10000
        });
        
        console.log('Statistics response:', statisticsResponse.data);
        setStatistics(statisticsResponse.data);
      } catch (err) {
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Combine publication types with their counts
  const publicationTypesWithCounts = publicationTypes.map(type => {
    const statsType = statistics?.publicationsByType?.find(stat => stat.id === type.id);
    return {
      id: type.id,
      name: type.name,
      count: statsType ? statsType.count : 0
    };
  });

  // Gradient colors for different publication types
  const getGradientColor = (index) => {
    const gradients = [
      'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', // Blue to Purple
      'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', // Pink to Red
      'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', // Blue to Cyan
      'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', // Green to Teal
      'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', // Pink to Yellow
      'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)', // Light Blue to Pink
      'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)', // Orange to Peach
      'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)'  // Pink to Light Pink
    ];
    return gradients[index % gradients.length];
  };

  // Redirect users with only Weekly Highlight access to the Weekly Highlights section
  if (hasWeeklyHighlightAccess && !isAdmin) {
    return <Navigate to="/dashboard/weekly-highlights" replace />;
  }

  return (
    <div className="dashboard-home">
      <div className="welcome-section">
        <h2>Welcome back, {user?.name}!</h2>
        <p>Manage your publications and system settings from this dashboard.</p>
      </div>
      
      <div className="stats-grid">
        <div className="stat-card">
          <h3>Your Role</h3>
          <p className="stat-value">{user?.role}</p>
        </div>
        <div className="stat-card">
          <h3>Department</h3>
          <p className="stat-value">{user?.department || 'Not Assigned'}</p>
        </div>
        <div className="stat-card">
          <h3>Publication Type</h3>
          <p className="stat-value">{user?.publicationType || 'Not Assigned'}</p>
        </div>
        <div className="stat-card">
          <h3>Status</h3>
          <p className="stat-value">{user?.isActive ? 'Active' : 'Inactive'}</p>
        </div>
      </div>

      {/* Publication Types Statistics Section */}
      <div className="publication-types-section">
        <h3 className="section-title">
          <FaChartBar /> Publication Types Overview
        </h3>
        
        {loading && (
          <div className="loading-message">
            <div className="loading-spinner"></div>
            <p>Loading publication statistics...</p>
          </div>
        )}
        
        {!loading && publicationTypesWithCounts.length === 0 && (
          <div className="empty-state">
            <p>No publication types found. Please add publication types in the Publication Types management section.</p>
          </div>
        )}
        
        {!loading && publicationTypesWithCounts.length > 0 && (
          <div className="publication-types-grid">
            {publicationTypesWithCounts.map((item, index) => (
              <div 
                key={item.id || index} 
                className="publication-type-card"
                style={{ 
                  background: getGradientColor(index),
                  transform: `translateZ(${index * 2}px)`
                }}
              >
                <div className="card-content">
                  <h4 className="publication-type-name">{item.name}</h4>
                  <div className="publication-count">
                    <span className="count-number">{item.count}</span>
                  </div>
                </div>
                <div className="card-shadow"></div>
              </div>
            ))}
          </div>
        )}

        {/* Summary Statistics */}
        {statistics && (
          <div className="summary-stats">
            <div className="summary-card total">
              <h4>Total Publications</h4>
              <p className="summary-number">{statistics.totalPublications || 0}</p>
            </div>
            <div className="summary-card recent">
              <h4>Recent (30 days)</h4>
              <p className="summary-number">{statistics.recentPublications || 0}</p>
            </div>
            <div className="summary-card users">
              <h4>Active Users</h4>
              <p className="summary-number">{statistics.activeUsers || 0}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard; 